import { CloseOutlined } from '@mui/icons-material';
import { Alert, Box, CircularProgress, IconButton } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import { useAccountUsersByOrganization } from '@/hooks/useAccountUsersByOrganisation';
import { useManageAccountUsersOrganization } from '@/hooks/useManageAccountUsersOrganization';
import { useSnackbar } from '@/hooks/useSnackbar';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

import AccountUserAutocompleteForOrganization, { EnhancedAccountUser } from './components/AccountUserAutocompleteForOrganization';

interface UserAccessTabProps {
  organizationId?: number;
}

type AccountUsersByOrganizationResponse = components['schemas']['GetAccountUsersByOrganizationQueryResponse'];

const columnHelper = createColumnHelper<AccountUsersByOrganizationResponse>();

function UserAccessTab({ organizationId }: UserAccessTabProps) {
  const snackbar = useSnackbar();

  const [searchParams, setSearchParams] = useSearchParams();

  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { accountUsers, totalCount, isLoading, error, isRefetching } = useAccountUsersByOrganization(
    apiPage,
    pagination.pageSize,
    organizationId || 0,
  );

  const {
    addUsers,
    removeUser,
    loading: isManagingUsers,
    removeUserMutation,
  } = useManageAccountUsersOrganization({
    snackbar,
    organizationId: organizationId ?? 0,
  });

  const ensureValidPageAfterRemove = useCallback(
    (newTotalCount: number) => {
      const maxPageIndex = Math.max(0, Math.ceil(newTotalCount / pagination.pageSize) - 1);

      if (pagination.pageIndex > maxPageIndex) {
        if (setPagination) {
          setPagination((prev) => ({
            ...prev,
            pageIndex: maxPageIndex,
          }));
        }
      }
    },
    [pagination.pageIndex, pagination.pageSize, setPagination],
  );

  const handleAddUsers = async (selectedUsers: EnhancedAccountUser[]) => {
    if (!organizationId || selectedUsers.length === 0 || isManagingUsers) {
      return;
    }

    try {
      const emailsToAdd = selectedUsers.map((u) => u.email).filter((email): email is string => email !== null && email !== undefined);

      const result = await addUsers(organizationId, emailsToAdd);

      if (result) {
        snackbar.showSuccess('Users added successfully');
      }
    } catch (err) {
      handleSnackbarError(err, snackbar, 'Failed to add users');
    }
  };

  const handleRemoveAccountUser = useCallback(
    async (email: string) => {
      if (!organizationId || !email || isManagingUsers) {
        return;
      }

      try {
        const success = await removeUser(organizationId, email);

        const newTotal = (totalCount ?? 0) - 1;

        if (success) {
          snackbar.showSuccess('Account Admin removed successfully');
          ensureValidPageAfterRemove(newTotal);
        }
      } catch (err) {
        handleSnackbarError(err, snackbar, 'Failed to remove Account Admin');
      }
    },
    [organizationId, removeUser, isManagingUsers, snackbar, ensureValidPageAfterRemove, totalCount],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
    const params = new URLSearchParams(searchParams);
    params.set('page', (newPage + 1).toString());

    setSearchParams(params, { replace: true });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newPageSize = parseInt(event.target.value, 10);

    setPagination({ pageIndex: 0, pageSize: newPageSize });

    const params = new URLSearchParams(searchParams);
    params.set('pageSize', newPageSize.toString());
    params.set('page', '1');

    setSearchParams(params, { replace: true });
  };

  // Define columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'Account Admin Email',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          const accountUser = info.row.original;
          const isRemovingCurrentUser =
            removeUserMutation.variables?.userEmail === accountUser.email && (isRefetching || removeUserMutation.isPending);

          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <IconButton
                size='small'
                onClick={() => {
                  if (!accountUser.email) return;
                  handleRemoveAccountUser(accountUser.email);
                }}
                disabled={!accountUser.email || isManagingUsers || isRemovingCurrentUser || removeUserMutation.isPending || isRefetching}
                data-testid={`remove-admin-users-${info.row.index}`}
                sx={{ padding: '4px' }}
              >
                {isRemovingCurrentUser ? <CircularProgress size={16} color='inherit' /> : <CloseOutlined sx={{ fontSize: '14px' }} />}
              </IconButton>
            </Box>
          );
        },
      }),
    ],
    [handleRemoveAccountUser, isManagingUsers, removeUserMutation.variables?.userEmail, removeUserMutation.isPending, isRefetching],
  );

  const tableWithData = useBaseTable<AccountUsersByOrganizationResponse>({
    tableData: accountUsers?.items || [],
    columns,
    isColumnOrderEnabled: false,
    isColumnVisibilityEnabled: false,
    isRowSelectionEnabled: false,
    hasPagination: true,
    pagination: pagination,
    onPaginationChange: setPagination,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    paginationCount: totalCount ?? 0,
    rowsPerPageOptions: [3, 10, 50, 100],
  });

  if (error) {
    return (
      <Alert severity='error' sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}>
        Error: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <AccountUserAutocompleteForOrganization organizationId={organizationId ?? 0} onApply={handleAddUsers} disabled={isManagingUsers} />

      <BaseTable<AccountUsersByOrganizationResponse>
        {...tableWithData}
        testId='admin-users-table'
        ariaLabel='Admin Users list'
        noDataMessage='No Account Admin emails are assigned to this client.'
        loadingData={{
          isLoading: isLoading,
          loadingMessage: 'Loading...',
        }}
      />
    </Box>
  );
}

export default UserAccessTab;
